/** @jsx jsx */
import { Interpolation, jsx } from '@emotion/core';
import { MicrosoftIcon } from './MicrosoftIcon';
import SSOButton, { getStartUrlForProvider } from '../SSOButton';
import { Border } from '@zapier/theme';

// If href is not specified in props, the default value `getStartUrlForProvider('microsoft')` will be used.
export type MicrosoftSSOButtonProps = {
  href?: string;
  label?: string;
  next?: string;
  onTrackSignupConversion?: (customUserId: string) => void | Promise<void>;
  onTrackSignupIntent?: () => void;
  passThrough?: boolean;
  shouldOpenInChildWindow?: boolean;
};

// The constants below are from Microsoft Guidelines:
// https://docs.microsoft.com/en-us/azure/active-directory/develop/howto-add-branding-in-azure-ad-apps#pictogram-and-sign-in-with-microsoft
const BACKGROUND_COLOR = '#2f2f2f';
const FONT_COLOR = '#fff';
const ICON_BACKGROUND_COLOR = '#fff';

const href = getStartUrlForProvider('microsoft');

const labelCss: Interpolation = {
  flex: 1,
  marginLeft: '-18px',
  textAlign: 'center',
};

const defaultCss: Interpolation = {
  alignItems: 'center',
  backgroundColor: BACKGROUND_COLOR,
  borderRadius: Border.radius.default,
  color: FONT_COLOR,
  display: 'flex',
  fontSize: 13,
  fontWeight: 'bold',
  padding: 10,
  position: 'relative',
  transition: 'all 100ms ease',
  WebkitTextFillColor: FONT_COLOR,

  '&:hover, &:focus': {
    background: BACKGROUND_COLOR,
    color: FONT_COLOR,
    filter: 'brightness(80%)',
    textDecoration: 'none',
    WebkitTextFillColor: FONT_COLOR,
  },
  '&::before': {
    backgroundColor: ICON_BACKGROUND_COLOR,
    borderRadius: 1,
    bottom: 2,
    content: '""',
    left: 2,
    position: 'absolute',
    top: 2,
    width: 36,
  },
};

const iconCss = {
  height: 20,
  width: 20,
  zIndex: 1,
};

export const MicrosoftSSOButton = ({
  label,
  ...props
}: MicrosoftSSOButtonProps) => {
  return (
    <SSOButton href={href} {...props}>
      <span css={defaultCss}>
        <MicrosoftIcon css={iconCss} />
        <span css={labelCss}>{label}</span>
      </span>
    </SSOButton>
  );
};

MicrosoftSSOButton.defaultProps = {
  label: 'Continue with Microsoft',
  shouldOpenInChildWindow: false, // the native Microsoft Teams app doesn't support opening additional windows
};
